import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mic, Code2, Building2, Briefcase, Database, Users, Loader2, ArrowRight, History, Crown, Lock, Cloud, Megaphone, Bot, Sparkles, ChevronDown, ChevronUp, Video, Timer, LayoutList, PlayCircle, FileText, X, Cog, Wand2, UserCircle2, Check, TrendingUp, Radar as RadarIcon, BrainCircuit, Terminal, Target, Swords } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UploadResumeForInterviewCard from "@/components/UploadResumeForInterviewCard";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const LIVE_AI_TYPES = ["technical", "sql"];
const DIFFICULTY_LEVEL = { Easy: 1, Medium: 2, Hard: 3 };

const TYPE_META = {
  technical: { icon: Code2, color: "amber", difficulty: "Medium", tag: "Adapts to your answers", desc: { data_cloud: "Deep-dive into Data Cloud technical concepts and troubleshooting.", sfmc: "Deep-dive into AMPscript, Journey Builder, and Automation Studio troubleshooting.", mc_next: "Deep-dive into DMOs, Identity Resolution, Segmentation, and Agentforce troubleshooting.", servicenow: "Deep-dive into GlideRecord, Business Rules, Flow Designer, and ITSM troubleshooting.", admin_dev: "Deep-dive into data model, security/sharing, automation, Apex, Triggers, and Integration troubleshooting.", universal: "Deep-dive technical questions tailored entirely to your resume's target role." } },
  scenario: { icon: Briefcase, color: "purple", difficulty: "Hard", tag: null, desc: { data_cloud: "Solve a real business scenario using Data Cloud architecture.", sfmc: "Solve a real marketing automation scenario using SFMC journeys.", mc_next: "Solve a real agentic marketing scenario using MC Next DMOs and flows.", servicenow: "Solve a real ITSM scenario using ServiceNow CMDB, Flow Designer, and AI Agents.", admin_dev: "Solve a real Salesforce Admin/Dev scenario — security model, automation, or Apex design.", universal: "Real-world work scenarios tailored entirely to your resume's target role." } },
  architecture: { icon: Building2, color: "emerald", difficulty: "Hard", tag: "AI reviews your diagram", desc: { data_cloud: "Design-level questions on Data Cloud solution architecture.", sfmc: "Design-level questions on SFMC data model, consent, and business units.", mc_next: "Design-level questions on MC Next DMOs, Business Units, and governance.", servicenow: "Design-level questions on CSDM, CMDB, Scoped Apps, and integration architecture.", admin_dev: "Design-level questions on data model, sharing/security, and Apex/Integration architecture.", universal: "High-level design/process questions tailored entirely to your resume's target role." } },
  sql: { icon: Database, color: "amber", difficulty: "Easy", tag: "Adapts to your answers", desc: { data_cloud: "SQL and Calculated Insights query design questions.", sfmc: "SQL against SFMC system Data Views (_Sent, _Open, _Click, etc).", mc_next: "SQL and query design against MC Next Individual/Engagement DMOs.", servicenow: "Query design against ServiceNow's incident/problem/change/CMDB tables.", admin_dev: "SOQL-style query design questions against standard Salesforce objects.", universal: "General SQL and data-querying questions relevant to your resume's target role." } },
  behavioral: { icon: Users, color: "rose", difficulty: "Easy", tag: null, desc: { data_cloud: "Soft-skills and STAR-format behavioral questions.", sfmc: "Soft-skills and STAR-format behavioral questions.", mc_next: "Soft-skills and STAR-format behavioral questions.", servicenow: "Soft-skills and STAR-format behavioral questions.", admin_dev: "Soft-skills and STAR-format behavioral questions.", universal: "Soft-skills and STAR-format behavioral questions tailored to your resume." } },
  coding: { icon: Terminal, color: "purple", difficulty: "Hard", tag: "Real code execution", desc: { data_cloud: "Live DSA coding round — write & run real Python/JS/Java/C++/C code, AI-reviewed.", sfmc: "Live DSA coding round — write & run real Python/JS/Java/C++/C code, AI-reviewed.", mc_next: "Live DSA coding round — write & run real Python/JS/Java/C++/C code, AI-reviewed.", servicenow: "Live DSA coding round — write & run real Python/JS/Java/C++/C code, AI-reviewed.", admin_dev: "Live DSA coding round — write & run real Python/JS/Java/C++/C code, AI-reviewed.", universal: "Live DSA coding round tailored to your resume's target role — real code execution, AI-reviewed." } },
};

const RADAR_DIM_LABELS = { structure: "Structure", clarity: "Clarity", confidence: "Confidence", problem_solving: "Problem-solving", communication: "Communication" };

const TYPE_BG_IMAGES = {
  technical: "https://images.unsplash.com/photo-1637166185518-058f5896a2e9?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  scenario: "https://images.unsplash.com/photo-1708779493105-9c743e367a3c?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  architecture: "https://images.unsplash.com/photo-1546146477-15a587cd3fcb?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  sql: "https://images.unsplash.com/photo-1615677679325-4a33c6d6b010?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  behavioral: "https://images.unsplash.com/photo-1736843638363-aeb3924788e2?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  coding: "https://images.unsplash.com/photo-1489875347897-49f64b51c1f8?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
};

const TRACKS = [
  { id: "universal", label: "My Resume", icon: FileText, accent: "rose" },
  { id: "data_cloud", label: "Data Cloud", icon: Cloud, accent: "indigo" },
  { id: "admin_dev", label: "Admin + Dev", icon: Cog, accent: "amber" },
  { id: "sfmc", label: "SFMC", icon: Megaphone, accent: "teal" },
  { id: "mc_next", label: "MC Next", icon: Bot, accent: "fuchsia" },
  { id: "servicenow", label: "ServiceNow", icon: Sparkles, accent: "emerald" },
];

const ACCENT = {
  teal: { text: "text-teal-300", bg: "bg-teal-400/10", border: "border-teal-400/40", iconBg: "bg-teal-400/20", glow: "shadow-[0_8px_24px_rgba(45,212,191,0.18)]" },
  fuchsia: { text: "text-fuchsia-300", bg: "bg-fuchsia-400/10", border: "border-fuchsia-400/40", iconBg: "bg-fuchsia-400/20", glow: "shadow-[0_8px_24px_rgba(232,121,249,0.18)]" },
  indigo: { text: "text-indigo-300", bg: "bg-indigo-400/10", border: "border-indigo-400/40", iconBg: "bg-indigo-400/20", glow: "shadow-[0_8px_24px_rgba(129,140,248,0.18)]" },
  emerald: { text: "text-emerald-300", bg: "bg-emerald-400/10", border: "border-emerald-400/40", iconBg: "bg-emerald-400/20", glow: "shadow-[0_8px_24px_rgba(52,211,153,0.18)]" },
  amber: { text: "text-amber-300", bg: "bg-amber-400/10", border: "border-amber-400/40", iconBg: "bg-amber-400/20", glow: "shadow-[0_8px_24px_rgba(251,191,36,0.18)]" },
  rose: { text: "text-rose-300", bg: "bg-rose-400/10", border: "border-rose-400/40", iconBg: "bg-rose-400/20", glow: "shadow-[0_8px_24px_rgba(251,113,133,0.18)]" },
};

const FREE_TIER_LIMIT = 3;

const TYPE_GLOW_HEX = { amber: "#f59e0b", purple: "#a855f7", emerald: "#10b981", rose: "#f43f5e" };

export default function InterviewCenterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [liveHistory, setLiveHistory] = useState([]);
  const [starting, setStarting] = useState(null);
  const [showJd, setShowJd] = useState(false);
  const [jdText, setJdText] = useState("");
  const [timedMode, setTimedMode] = useState(false);
  const [panelMode, setPanelMode] = useState(false);
  const [resumeContext, setResumeContext] = useState(location.state?.resumeContext || null);
  const [resumeTargetRole, setResumeTargetRole] = useState(location.state?.targetRole || "");
  const [track, setTrack] = useState(() => {
    if (location.state?.resumeContext) return "universal";
    const requested = searchParams.get("track");
    return TRACKS.some((t) => t.id === requested) ? requested : "data_cloud";
  });
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [intentText, setIntentText] = useState("");
  const [parsingIntent, setParsingIntent] = useState(false);
  const [intentResult, setIntentResult] = useState(null);
  const [highlightType, setHighlightType] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [resumeSkills, setResumeSkills] = useState([]);
  const [seniorityLevel, setSeniorityLevel] = useState(null);
  const [autoFetchedResume, setAutoFetchedResume] = useState(false);
  const [loadingResumeIntel, setLoadingResumeIntel] = useState(false);

  useEffect(() => {
    api.get("/interview/types").then((r) => setTypes(r.data));
    api.get("/interview/history/me").then((r) => setHistory(r.data));
    api.get("/voice-interview/history/me").then((r) => setLiveHistory(r.data)).catch(() => setLiveHistory([]));
    api.get("/interview/personas").then((r) => setPersonas(r.data)).catch(() => setPersonas([]));
  }, []);

  useEffect(() => {
    api.get("/interview/analytics/me", { params: { track } }).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null));
  }, [track]);

  const [memory, setMemory] = useState(null);
  useEffect(() => {
    api.get("/interview/memory/me").then((r) => setMemory(r.data)).catch(() => setMemory(null));
  }, []);

  // AI Resume Intelligence: if the learner already has a saved resume (built inside Cloudivo's
  // Resume Builder) with a target role set, auto-detect it the moment they land on the "My Resume"
  // track instead of always forcing a fresh file upload.
  useEffect(() => {
    if (track === "universal" && !resumeContext && !resumeTargetRole && !autoFetchedResume) {
      setAutoFetchedResume(true);
      setLoadingResumeIntel(true);
      api.get("/resume-builder/interview-context").then((r) => {
        if (r.data.resume_context) {
          setResumeContext(r.data.resume_context);
          setResumeTargetRole(r.data.target_role || "");
          setResumeSkills(r.data.skills || []);
          setSeniorityLevel(r.data.seniority_level || null);
        }
      }).finally(() => setLoadingResumeIntel(false));
    }
  }, [track, resumeContext, resumeTargetRole, autoFetchedResume]);

  const isPremium = !!user?.is_premium;
  const completedCount = history.length + liveHistory.length;
  const remaining = Math.max(0, FREE_TIER_LIMIT - completedCount);
  const limitReached = !isPremium && remaining === 0;

  const combinedHistory = [
    ...history.map((s) => ({ id: s.id, type: s.type, track: s.track, completed_at: s.completed_at, score: s.result?.overall_score, live: false })),
    ...liveHistory.map((s) => ({ id: s.id, type: s.type, track: s.track, completed_at: s.completed_at, score: s.scorecard?.overall_score, live: true })),
  ].sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

  const deltaByHistoryId = {};
  {
    const chronological = [...combinedHistory].sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));
    const lastScoreByType = {};
    chronological.forEach((s) => {
      const prev = lastScoreByType[s.type];
      deltaByHistoryId[s.id] = prev != null && s.score != null ? s.score - prev : null;
      lastScoreByType[s.type] = s.score;
    });
  }

  const startInterview = async (type) => {
    setStarting(type);
    try {
      if (LIVE_AI_TYPES.includes(type)) {
        const mode = panelMode && type === "technical" ? "panel" : "solo";
        const combinedJd = [jdText, resumeContext].filter(Boolean).join("\n\n") || undefined;
        const { data } = await api.post("/voice-interview/start", { type, track, jd_text: combinedJd, mode, timed_mode: timedMode, target_role: resumeTargetRole || undefined, persona: selectedPersona || undefined, seniority_level: seniorityLevel || undefined });
        navigate(`/interview/live/${data.session_id}`, { state: data });
      } else {
        const { data } = await api.post("/interview/start", { type, track, resume_context: resumeContext || undefined, target_role: resumeTargetRole || undefined, persona: selectedPersona || undefined, seniority_level: seniorityLevel || undefined });
        navigate(`/interview/session/${data.session_id}`);
      }
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("You've used all your free interviews. Upgrade to Premium for unlimited access!");
        navigate("/premium");
      } else {
        toast.error(e.response?.data?.detail || "Could not start interview");
      }
      setStarting(null);
    }
  };

  const parseIntent = async () => {
    if (!intentText.trim()) return;
    setParsingIntent(true);
    setIntentResult(null);
    try {
      const { data } = await api.post("/interview/parse-intent", { text: intentText });
      setIntentResult(data);
      setTrack(data.track);
      if (data.track === "universal" && data.target_role) setResumeTargetRole(data.target_role);
      setSelectedPersona(data.persona || null);
      setHighlightType(data.interview_type);
      toast.success("Got it! Review the pre-filled selections below, then hit Start on the highlighted card.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't understand that — try rephrasing, e.g. \"Take my Python Developer interview\"");
    } finally {
      setParsingIntent(false);
    }
  };

  const showSetup = !(track === "universal" && !resumeContext && !resumeTargetRole);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="interview-center-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center">
              <Mic className="w-4.5 h-4.5 text-primary" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">AI-Powered</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Interview Center</h1>
          <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-xl">Practice real interviews with an AI hiring manager and a talking avatar interviewer. Get scored on technical depth, communication, problem solving, and confidence.</p>
        </div>
        {isPremium ? (
          <span className="flex items-center gap-1.5 text-xs font-bold bg-amber-400/10 border border-amber-400/25 text-amber-300 px-4 py-2 rounded-full shadow-[0_0_20px_rgba(251,191,36,0.15)]" data-testid="interview-premium-badge">
            <Crown className="w-3.5 h-3.5" /> Premium — Unlimited
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 border border-white/10 text-slate-300 px-4 py-2 rounded-full" data-testid="interview-quota-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> {remaining} of {FREE_TIER_LIMIT} free interviews left
          </span>
        )}
      </div>

      {limitReached && (
        <div className="glass-panel border border-amber-400/20 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="interview-limit-reached-banner">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-400 shrink-0" />
            <p className="text-sm text-slate-300">You've used all {FREE_TIER_LIMIT} free mock interviews. Upgrade to Premium for unlimited interviews.</p>
          </div>
          <Link to="/premium" data-testid="interview-upgrade-link" className="flex items-center gap-1.5 text-xs font-bold bg-amber-400 hover:bg-amber-300 text-background px-4 py-2 rounded-lg shrink-0 transition-colors">
            <Crown className="w-3.5 h-3.5" /> Upgrade to Premium
          </Link>
        </div>
      )}

      {/* Hero: Natural language intent */}
      <div className="relative overflow-hidden glass-panel rounded-3xl p-6 sm:p-10 mb-8" data-testid="interview-intent-box">
        <div className="absolute -top-24 -right-16 w-72 h-72 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-primary flex items-center justify-center gap-1.5 mb-4">
            <Wand2 className="w-3.5 h-3.5" /> Just tell the AI what you want
          </p>
          <div className="relative flex items-center">
            <input
              value={intentText} onChange={(e) => setIntentText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && parseIntent()}
              placeholder='e.g. "Take my Python Developer interview" or "Amazon Bar Raiser interview for Data Cloud"'
              data-testid="interview-intent-input"
              className="w-full h-14 bg-white/[0.04] border border-white/10 rounded-2xl pl-5 pr-16 sm:pr-40 text-sm sm:text-base text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/40 transition-shadow"
            />
            <button
              onClick={parseIntent} disabled={parsingIntent || !intentText.trim()} data-testid="interview-intent-submit-btn"
              className="absolute right-2 h-10 flex items-center gap-1.5 text-xs sm:text-sm font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50 text-white px-3.5 sm:px-5 rounded-xl shadow-[0_0_16px_rgba(77,151,255,0.35)] transition-colors"
            >
              {parsingIntent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} <span className="hidden sm:inline">Understand</span>
            </button>
          </div>
          <AnimatePresence>
            {intentResult && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2 flex-wrap mt-4 text-xs" data-testid="interview-intent-result-chip"
              >
                <span className="flex items-center gap-1.5 bg-emerald-400/10 border border-emerald-400/25 text-emerald-300 px-3 py-1.5 rounded-full font-semibold capitalize">
                  <Check className="w-3.5 h-3.5" /> {intentResult.interview_type}
                </span>
                <span className="text-slate-300 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">{intentResult.target_role || TRACKS.find((t) => t.id === intentResult.track)?.label}</span>
                {intentResult.persona_label && (
                  <span className="text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full">{intentResult.persona_label}</span>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          {analytics?.insight_text && (
            <p className="flex items-center justify-center gap-1.5 mt-4 text-xs text-emerald-300" data-testid="interview-weekly-insight">
              <TrendingUp className="w-3.5 h-3.5" /> {analytics.insight_text}
            </p>
          )}
        </div>
      </div>

      {/* Track selector */}
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Step 1 · Choose what to practice</p>
        <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-thin" data-testid="interview-track-selector">
          {TRACKS.map((t) => {
            const Icon = t.icon;
            const active = track === t.id;
            const a = ACCENT[t.accent];
            return (
              <button
                key={t.id} onClick={() => setTrack(t.id)} data-testid={`interview-track-${t.id}`}
                className={`flex flex-col items-center gap-2 shrink-0 w-[108px] py-4 rounded-2xl border transition-all ${
                  active ? `${a.bg} ${a.border} -translate-y-0.5 ${a.glow}` : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:-translate-y-0.5"
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${active ? a.iconBg : "bg-white/5"}`}>
                  <Icon className={`w-4.5 h-4.5 ${active ? a.text : "text-slate-400"}`} />
                </div>
                <span className={`text-xs font-semibold ${active ? "text-white" : "text-slate-400"}`}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {resumeContext && (
        <div className="glass-panel border border-rose-400/20 rounded-2xl p-4 mb-6" data-testid="interview-resume-context-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-slate-300">
                  <span className="text-primary font-semibold">AI Resume Intelligence</span> personalized this using your resume{resumeTargetRole ? ` — Target Role: ${resumeTargetRole}` : ""}. Questions will reflect your skills &amp; experience.
                </p>
                {(seniorityLevel || resumeSkills.length > 0) && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="interview-resume-intel-tags">
                    {seniorityLevel && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-indigo-400/15 text-indigo-300" data-testid="interview-resume-seniority-badge">{seniorityLevel}</span>
                    )}
                    {resumeSkills.slice(0, 6).map((s) => (
                      <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">{s}</span>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => { setResumeContext(null); setResumeTargetRole(""); setResumeSkills([]); setSeniorityLevel(null); }}
                  data-testid="interview-resume-use-different-btn"
                  className="text-[11px] text-slate-500 hover:text-white mt-2 underline"
                >
                  Not accurate? Upload a different resume
                </button>
              </div>
            </div>
            <button onClick={() => setResumeContext(null)} data-testid="interview-resume-context-dismiss" className="text-slate-400 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {track === "universal" && !resumeContext && !resumeTargetRole && (
        loadingResumeIntel ? (
          <div className="glass-panel rounded-2xl p-8 mb-6 text-center" data-testid="interview-resume-intel-loading">
            <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-400">AI Resume Intelligence is checking for your saved resume…</p>
          </div>
        ) : (
          <UploadResumeForInterviewCard
            onExtracted={(data) => {
              setResumeContext(data.resume_context);
              setResumeTargetRole(data.target_role || "");
              setResumeSkills(data.skills || []);
              setSeniorityLevel(data.seniority_level || null);
            }}
          />
        )
      )}

      {resumeTargetRole && !resumeContext && (
        <div className="glass-panel border border-indigo-400/20 rounded-2xl p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="interview-target-role-banner">
          <div className="flex items-center gap-3">
            <Wand2 className="w-5 h-5 text-indigo-400 shrink-0" />
            <p className="text-sm text-slate-300">Target Role: <span className="text-white font-semibold">{resumeTargetRole}</span>. Or upload your resume for even more personalized questions.</p>
          </div>
          <button onClick={() => setResumeTargetRole("")} data-testid="interview-target-role-dismiss" className="text-slate-400 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {showSetup && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: interview type cards */}
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Step 2 · Pick your interview format &amp; start</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {types.map((t, idx) => {
                const meta = TYPE_META[t.id] || TYPE_META.technical;
                const Icon = meta.icon;
                const isLive = LIVE_AI_TYPES.includes(t.id);
                const glow = TYPE_GLOW_HEX[meta.color] || TYPE_GLOW_HEX.amber;
                return (
                  <motion.div
                    key={t.id}
                    data-testid={`interview-type-card-${t.id}`}
                    className={`relative overflow-hidden rounded-2xl border ${isLive ? "border-primary/30" : "border-white/10"} ${highlightType === t.id ? "ring-2 ring-emerald-400/60" : ""} bg-white/[0.03] p-5 flex flex-col gap-3 min-h-[208px]`}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: idx * 0.05 }}
                    whileHover={{ y: -4, boxShadow: `0 16px 32px rgba(1,4,12,0.5), 0 0 0 1px ${glow}30` }}
                  >
                    <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${TYPE_BG_IMAGES[t.id]})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d1017] via-[#0d1017]/75 to-[#0d1017]/25" />
                    <div className="relative flex items-start justify-between gap-2">
                      <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="flex items-center gap-1 text-[10px] font-bold uppercase bg-purple-400/20 text-purple-200 px-1.5 py-0.5 rounded-full backdrop-blur" data-testid={`interview-video-badge-${t.id}`}>
                          <Video className="w-2.5 h-2.5" /> AI Video Interviewer
                        </span>
                        {isLive && (
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase bg-primary/25 text-white px-1.5 py-0.5 rounded-full backdrop-blur" data-testid={`interview-live-badge-${t.id}`}>
                            <Mic className="w-2.5 h-2.5" /> Live AI Voice
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="relative flex-1">
                      <p className="text-base font-semibold text-white">{t.label}</p>
                      <p className="text-xs text-slate-300/90 mt-1 leading-relaxed">{meta.desc[track]}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`interview-meta-row-${t.id}`}>
                        <span className="flex items-center gap-1">
                          {[1, 2, 3].map((i) => (
                            <span key={i} className={`w-1 h-1 rounded-full ${i <= DIFFICULTY_LEVEL[meta.difficulty] ? "bg-slate-300" : "bg-white/15"}`} />
                          ))}
                          <span className="text-[10px] text-slate-400 ml-1">{meta.difficulty}</span>
                        </span>
                        {meta.tag && (
                          <span className="flex items-center gap-1 text-[10px] text-primary/90"><Sparkles className="w-2.5 h-2.5" /> {meta.tag}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => startInterview(t.id)} disabled={starting === t.id} data-testid={`interview-start-btn-${t.id}`}
                      className="relative flex items-center justify-center gap-1.5 text-xs font-bold text-white px-4 py-2.5 rounded-full w-full transition-all hover:brightness-110 disabled:opacity-60"
                      style={{ backgroundColor: glow }}
                    >
                      {starting === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Start Interview
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Right: sidebar — persona, modes, JD */}
          <div className="lg:col-span-4">
            <div className="space-y-5 lg:sticky lg:top-6">
              <div className="glass-panel rounded-2xl p-5" data-testid="interview-skill-radar-card">
                <p className="text-xs font-semibold text-slate-200 mb-3 flex items-center gap-1.5"><RadarIcon className="w-3.5 h-3.5 text-primary" /> Skill Radar</p>
                {analytics?.sample_size > 0 ? (
                  <>
                    <div className="h-40 -mx-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={Object.entries(analytics.skill_radar).map(([k, v]) => ({ dim: RADAR_DIM_LABELS[k], value: v ?? 0 }))} outerRadius="70%">
                          <PolarGrid stroke="rgba(255,255,255,0.1)" />
                          <PolarAngleAxis dataKey="dim" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                          <Radar dataKey="value" stroke="#4d97ff" fill="#4d97ff" fillOpacity={0.35} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1" data-testid="interview-analytics-caption">
                      Based on your last {analytics.sample_size} interview{analytics.sample_size === 1 ? "" : "s"}.
                      {analytics.percentile != null && (
                        <> You're scoring higher than <span className="text-primary font-semibold">{analytics.percentile}%</span> of {TRACKS.find((t) => t.id === track)?.label} candidates.</>
                      )}
                    </p>
                    {Object.entries(analytics.skill_radar).some(([, v]) => v === null) && (
                      <p className="text-[10px] text-slate-600 mt-1.5">Mix in a few Classic + Live Voice interviews to unlock every dimension.</p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-slate-500 py-6 text-center">Complete your first interview to unlock your personal Skill Radar.</p>
                )}
              </div>

              <div className="glass-panel rounded-2xl p-5" data-testid="interview-memory-card">
                <p className="text-xs font-semibold text-slate-200 mb-1 flex items-center gap-1.5"><BrainCircuit className="w-3.5 h-3.5 text-primary" /> Your AI Memory Twin</p>
                <p className="text-[11px] text-slate-500 mb-3">Cloudivo remembers your past interviews and adapts new ones around it</p>
                {memory?.sessions_analyzed > 0 && (memory.weak_areas?.length > 0 || memory.strong_areas?.length > 0) ? (
                  <div className="space-y-2" data-testid="interview-memory-tags">
                    {(memory.weak_areas || []).map((w) => (
                      <div key={w.topic} className="flex items-center justify-between gap-2 text-xs" data-testid={`interview-memory-weak-${w.topic.replace(/\s+/g, "-").toLowerCase()}`}>
                        <span className="text-slate-300 truncate">{w.topic}</span>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0 ${
                          w.status === "resolved" ? "bg-emerald-400/15 text-emerald-300" : w.status === "improving" ? "bg-indigo-400/15 text-indigo-300" : "bg-amber-400/15 text-amber-300"
                        }`}>{w.status} · {w.times_flagged}x</span>
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-600 pt-1">Based on {memory.sessions_analyzed} tracked session{memory.sessions_analyzed === 1 ? "" : "s"}. Future interviews will revisit these areas.</p>

                    {memory.recommended_practice_plan?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5 space-y-2" data-testid="interview-practice-plan">
                        <p className="text-[11px] font-semibold text-slate-200 flex items-center gap-1.5"><Target className="w-3 h-3 text-primary" /> Recommended Practice Plan</p>
                        {memory.recommended_practice_plan.map((p) => (
                          <Link
                            key={p.day}
                            to={p.link}
                            data-testid={`practice-plan-day-${p.day}`}
                            className="flex items-start gap-2 p-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors group"
                          >
                            {p.day % 5 === 0 ? <Swords className="w-3.5 h-3.5 text-rose-400 shrink-0 mt-0.5" /> : <ArrowRight className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform" />}
                            <div className="min-w-0">
                              <p className="text-[11px] text-slate-200 font-medium truncate">Day {p.day}{p.day % 5 === 0 ? " · Boss Battle" : ""} — {p.title}</p>
                              <p className="text-[10px] text-slate-500 leading-snug">{p.reason}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 py-4 text-center">Complete a couple of interviews to build your AI Memory Twin — it'll start revisiting your weak spots automatically.</p>
                )}
              </div>

              <div className="glass-panel rounded-2xl p-5" data-testid="interview-persona-picker">
                <p className="text-xs font-semibold text-slate-200 mb-1 flex items-center gap-1.5"><UserCircle2 className="w-3.5 h-3.5 text-primary" /> Choose Your Interviewer</p>
                <p className="text-[11px] text-slate-500 mb-4">Optional — applies to Classic &amp; Live Solo interviews</p>
                <TooltipProvider delayDuration={150}>
                  <div className="grid grid-cols-4 gap-3">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button onClick={() => setSelectedPersona(null)} data-testid="interview-persona-none" className="flex flex-col items-center gap-1.5 group">
                          <span className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all ${!selectedPersona ? "border-primary ring-2 ring-primary/40 ring-offset-2 ring-offset-[#131a29] bg-primary/20 text-primary" : "border-white/10 bg-white/5 text-slate-400 group-hover:border-white/30"}`}>
                            <UserCircle2 className="w-5 h-5" />
                          </span>
                          <span className={`text-[10px] font-medium ${!selectedPersona ? "text-white" : "text-slate-500"}`}>Default</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p className="max-w-[160px]">Balanced, general AI hiring manager</p></TooltipContent>
                    </Tooltip>
                    {personas.map((p) => {
                      const initials = p.label.split(" ").map((w) => w[0]).slice(0, 2).join("");
                      const active = selectedPersona === p.id;
                      return (
                        <Tooltip key={p.id}>
                          <TooltipTrigger asChild>
                            <button onClick={() => setSelectedPersona(p.id)} data-testid={`interview-persona-${p.id}`} className="flex flex-col items-center gap-1.5 group">
                              <span className={`w-11 h-11 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all ${active ? "border-primary ring-2 ring-primary/40 ring-offset-2 ring-offset-[#131a29] bg-primary/20 text-primary" : "border-white/10 bg-white/5 text-slate-300 group-hover:border-white/30"}`}>
                                {initials}
                              </span>
                              <span className={`text-[10px] font-medium text-center leading-tight ${active ? "text-white" : "text-slate-500"}`}>{p.label.split(" ")[0]}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent><p className="max-w-[180px] text-xs">{p.label} — {p.blurb}</p></TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>
                {selectedPersona && (
                  <p className="text-[11px] text-slate-500 mt-3 pt-3 border-t border-white/5" data-testid="interview-persona-blurb">{personas.find((p) => p.id === selectedPersona)?.blurb}</p>
                )}
              </div>

              <div className="glass-panel rounded-2xl p-5" data-testid="interview-mode-toggles">
                <p className="text-xs font-semibold text-slate-200 mb-4">Interview Modes</p>
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <Timer className="w-4 h-4 text-amber-400 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-white">Timed Mode</p>
                        <p className="text-[10px] text-slate-500">Adds a countdown per question</p>
                      </div>
                    </div>
                    <Switch checked={timedMode} onCheckedChange={setTimedMode} data-testid="interview-timed-mode-toggle" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <LayoutList className="w-4 h-4 text-primary shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-white">Panel Interview</p>
                        <p className="text-[10px] text-slate-500">3 rounds, 3 personas (Technical only)</p>
                      </div>
                    </div>
                    <Switch checked={panelMode} onCheckedChange={setPanelMode} data-testid="interview-panel-mode-toggle" />
                  </div>
                </div>
              </div>

              <div className="glass-panel rounded-2xl p-5">
                <button onClick={() => setShowJd((v) => !v)} data-testid="interview-jd-toggle-btn" className="flex items-center justify-between w-full text-xs font-semibold text-slate-200 hover:text-white">
                  <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Tailor to a Job Description</span>
                  {showJd ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                <AnimatePresence>
                  {showJd && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <textarea
                        value={jdText}
                        onChange={(e) => setJdText(e.target.value)}
                        rows={5}
                        placeholder="Paste a job description here — the Live AI Interview (Technical / SQL) will tailor its questions to match it..."
                        data-testid="interview-jd-textarea"
                        className="w-full mt-3 neu-inset rounded-xl px-3 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-primary/50 border-none"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-14">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-3"><History className="w-3.5 h-3.5" /> Past Interviews</p>
        <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="interview-history-list">
          {combinedHistory.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No interviews completed yet.</p>}
          {combinedHistory.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3.5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors" data-testid={`interview-history-row-${s.id}`}>
              <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold font-mono shrink-0 ${
                s.score >= 75 ? "bg-emerald-400/15 text-emerald-300" : s.score >= 50 ? "bg-amber-400/15 text-amber-300" : "bg-rose-400/15 text-rose-300"
              }`}>{s.score}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary">{s.type}</span>
                  {s.live && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-400/10 text-emerald-300">Live</span>}
                  <span className="text-xs text-slate-400">{TRACKS.find((t) => t.id === s.track)?.label || s.track}</span>
                </div>
                <p className="text-[11px] text-slate-500 font-mono mt-0.5">{new Date(s.completed_at).toLocaleDateString()}</p>
              </div>
              {deltaByHistoryId[s.id] != null && (
                <span className={`text-[11px] font-semibold font-mono shrink-0 hidden sm:inline ${deltaByHistoryId[s.id] >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid={`interview-history-delta-${s.id}`}>
                  {deltaByHistoryId[s.id] >= 0 ? "+" : ""}{deltaByHistoryId[s.id]} vs last attempt
                </span>
              )}
              <button
                onClick={() => navigate(`/interview/replay/${s.live ? "live" : "classic"}/${s.id}`)}
                data-testid={`interview-replay-btn-${s.id}`}
                className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 shrink-0"
              >
                <PlayCircle className="w-3.5 h-3.5" /> Replay
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
